import React, { ReactNode, useMemo } from "react";

import Tooltip from "antd/es/tooltip";
import { MessageDescriptor, useIntl } from "react-intl";

import { Button, Popconfirm } from "@mapmycustomers/ui";

import EmailDraftInfo from "@app/types/email/EmailDraftInfo";

import messages from "./messages";

interface DraftApplyButtonProps {
  draftInfo?: EmailDraftInfo;
  onApply?: () => void;
}

const DraftApplyButton = ({ draftInfo, onApply }: DraftApplyButtonProps) => {
  const intl = useIntl();

  const recipientsValidation = useMemo(() => {
    return (draftInfo?.draft.metadata.recipients.to ?? []).reduce(
      (result, recipient) => {
        const entity = (draftInfo?.entities ?? []).find((entity) => entity.id === recipient.id);
        if (!entity) {
          result.someNotFound = true;
        }
        if (entity && !entity.email) {
          result.someWithoutEmail = true;
        }
        return result;
      },
      { someNotFound: false, someWithoutEmail: false }
    );
  }, [draftInfo]);

  let confirmDescriptionMessage: MessageDescriptor | undefined;
  if (recipientsValidation.someNotFound && recipientsValidation.someWithoutEmail) {
    confirmDescriptionMessage = messages.someRecipientsNotFoundOrWithoutEmailAlert;
  } else if (recipientsValidation.someNotFound) {
    confirmDescriptionMessage = messages.someRecipientsNotFoundAlert;
  } else if (recipientsValidation.someWithoutEmail) {
    confirmDescriptionMessage = messages.someRecipientsWithoutEmailAlert;
  }

  const renderButton = (onClick?: () => void) => {
    const disabledBecauseOfResources = !!draftInfo?.invalidResourceIds.length;

    return (
      <Tooltip
        title={
          disabledBecauseOfResources
            ? intl.formatMessage(messages.applyDraftDisabledBecauseOfResourcesTooltip)
            : undefined
        }
      >
        <span>
          <Button disabled={disabledBecauseOfResources} onClick={onClick} type="primary">
            {intl.formatMessage(messages.applyDraft)}
          </Button>
        </span>
      </Tooltip>
    );
  };

  if (!confirmDescriptionMessage) {
    return renderButton(onApply);
  }

  return (
    <Popconfirm
      description={intl.formatMessage(confirmDescriptionMessage, {
        li: (text: ReactNode) => <li>{text}</li>,
        // eslint-disable-next-line id-length -- FormatJS rich-text tag name must match the <p> tag in the localized message
        p: (text: ReactNode) => <p>{text}</p>,
        ul: (text: ReactNode) => <ul>{text}</ul>,
      })}
      onConfirm={onApply}
    >
      {renderButton()}
    </Popconfirm>
  );
};

export default DraftApplyButton;
